import { useEffect, useRef } from 'react'
import type { CSSProperties, RefObject } from 'react'

import { debounce } from 'src/utils/debounce'

const DEBOUNCE_DELAY = 200

type UseCardHeightSyncProps = {
  cardId: string
  containerTopOffset?: number
  backgroundHeightOffset?: number
  registerHeight: (id: string, height: number) => void
  maxHeight: number
}

type UseCardHeightSyncReturn = {
  containerRef: RefObject<HTMLDivElement>
  backgroundRef: RefObject<HTMLDivElement>
  rootRef: RefObject<HTMLDivElement>
  containerStyle: CSSProperties | undefined
  backgroundStyle: CSSProperties | undefined
  rootStyle: CSSProperties | undefined
}

export const useCardHeightSync = ({
  cardId,
  containerTopOffset = 10,
  backgroundHeightOffset = 15,
  registerHeight,
  maxHeight,
}: UseCardHeightSyncProps): UseCardHeightSyncReturn => {
  const containerRef = useRef<HTMLDivElement>(null)
  const backgroundRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const registerHeightRef = useRef(registerHeight)

  useEffect(() => {
    registerHeightRef.current = registerHeight

    if (!containerRef.current) {
      return
    }

    const measureHeight = () => {
      if (!containerRef.current) {
        return
      }

      const element = containerRef.current
      // Temporarily remove height constraint to measure natural height
      const originalHeight = element.style.height
      const originalMinHeight = element.style.minHeight
      const originalMaxHeight = element.style.maxHeight

      element.style.height = 'auto'
      element.style.minHeight = 'auto'
      element.style.maxHeight = 'none'

      // Use requestAnimationFrame to ensure layout is recalculated
      requestAnimationFrame(() => {
        if (!containerRef.current) {
          return
        }

        const containerHeight = containerRef.current.offsetHeight

        // Restore original styles
        containerRef.current.style.height = originalHeight
        containerRef.current.style.minHeight = originalMinHeight
        containerRef.current.style.maxHeight = originalMaxHeight

        if (containerHeight > 0) {
          registerHeightRef.current(cardId, containerHeight)
        }
      })
    }

    const debouncedMeasureHeight = debounce(measureHeight, DEBOUNCE_DELAY)

    const resizeObserver = new ResizeObserver(() => {
      debouncedMeasureHeight()
    })

    resizeObserver.observe(containerRef.current)
    window.addEventListener('orientationchange', debouncedMeasureHeight)

    // Initial measurement on mount
    const timeoutId = setTimeout(measureHeight, 100)

    return () => {
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
      window.removeEventListener('orientationchange', debouncedMeasureHeight)
    }
  }, [cardId, registerHeight])

  const containerStyle =
    maxHeight > 0
      ? {
          height: `${maxHeight}px`,
        }
      : undefined

  const backgroundStyle = maxHeight > 0 ? { height: `${maxHeight + backgroundHeightOffset}px` } : undefined
  const rootStyle = maxHeight > 0 ? { minHeight: `${containerTopOffset + maxHeight + 3}px` } : undefined

  return {
    containerRef,
    backgroundRef,
    rootRef,
    containerStyle,
    backgroundStyle,
    rootStyle,
  }
}
